/**
 * Access & Benefits configuration — the ONLY place benefit-type rules and the
 * legacy coupon → benefit mapping are declared.
 *
 * Prices still live in `pricing-config.ts`. This file says who may claim what,
 * how often, and through which route.
 */

import type { BenefitType, BenefitTypeRule } from "./benefit-types";

/** Bump on any change to benefit rules or precedence. */
export const BENEFIT_VERSION = "1.0.0";

/** Configurable rules per benefit type. */
export const BENEFIT_TYPE_RULES: Record<BenefitType, BenefitTypeRule> = {
  PUBLIC_CAMPAIGN: {
    type: "PUBLIC_CAMPAIGN",
    label: "Public campaign",
    layer: "campaign",
    claimMode: "code",
    isPublic: true,
    timeLimited: true,
    maxUsesPerUser: 1,
    maxUsesPerEmail: 1,
    maxGlobalUses: null,
    stackable: false,
    priority: 10,
    internalOnly: false,
    description:
      "Open to everyone inside a time window. Unlimited people, one redemption per account.",
  },
  SCHOLARSHIP: {
    type: "SCHOLARSHIP",
    label: "Scholarship",
    layer: "scholarship",
    claimMode: "either",
    isPublic: false,
    timeLimited: false,
    maxUsesPerUser: 1,
    maxUsesPerEmail: 1,
    maxGlobalUses: null,
    stackable: false,
    priority: 20,
    internalOnly: false,
    description:
      "Granted to an account after an application is approved. No code needed; legacy codes still work during migration.",
  },
  PRIVATE_INVITATION: {
    type: "PRIVATE_INVITATION",
    label: "Private invitation",
    layer: "coupon",
    claimMode: "code",
    isPublic: false,
    timeLimited: true,
    maxUsesPerUser: 1,
    maxUsesPerEmail: 1,
    maxGlobalUses: null,
    stackable: false,
    priority: 30,
    internalOnly: false,
    description:
      "A unique code issued to one person — friends, family, partner invites. Independently revocable.",
  },
  REFERRAL: {
    type: "REFERRAL",
    label: "Referral reward",
    layer: "coupon",
    claimMode: "either",
    isPublic: false,
    timeLimited: false,
    maxUsesPerUser: 1,
    maxUsesPerEmail: 1,
    maxGlobalUses: null,
    stackable: false,
    priority: 40,
    internalOnly: false,
    description: "Earned by being referred by an existing learner.",
  },
  AMBASSADOR: {
    type: "AMBASSADOR",
    label: "Ambassador reward",
    layer: "coupon",
    claimMode: "either",
    isPublic: false,
    timeLimited: false,
    maxUsesPerUser: 1,
    maxUsesPerEmail: 1,
    maxGlobalUses: null,
    stackable: false,
    priority: 45,
    internalOnly: false,
    description: "Held by campus ambassadors and community leads.",
  },
  PARTNER: {
    type: "PARTNER",
    label: "Partner pricing",
    layer: "partner",
    claimMode: "entitlement",
    isPublic: false,
    timeLimited: false,
    maxUsesPerUser: 1,
    maxUsesPerEmail: 1,
    maxGlobalUses: null,
    stackable: false,
    priority: 50,
    internalOnly: false,
    description:
      "Attached to a Partner ID. Children linked to the partner are priced automatically.",
  },
  INSTITUTION: {
    type: "INSTITUTION",
    label: "Institution pricing",
    layer: "institution",
    claimMode: "entitlement",
    isPublic: false,
    timeLimited: false,
    maxUsesPerUser: 1,
    maxUsesPerEmail: 1,
    maxGlobalUses: null,
    stackable: false,
    priority: 60,
    internalOnly: false,
    description:
      "Attached to an Institution ID — schools, universities, NGOs, government programmes.",
  },
  STAFF: {
    type: "STAFF",
    label: "Staff access",
    layer: "partner",
    claimMode: "entitlement",
    isPublic: false,
    timeLimited: false,
    maxUsesPerUser: 1,
    maxUsesPerEmail: 1,
    maxGlobalUses: null,
    stackable: false,
    priority: 70,
    internalOnly: true,
    description: "Internal team pricing. Never discoverable.",
  },
  ADMIN: {
    type: "ADMIN",
    label: "Testing",
    layer: "coupon",
    claimMode: "code",
    isPublic: true,
    timeLimited: false,
    maxUsesPerUser: 100,
    maxUsesPerEmail: 100,
    maxGlobalUses: null,
    stackable: false,
    // Open to anyone: TEST99 is a shared low-value test code, not admin-gated.
    internalOnly: false,
    priority: 80,
    description: "Low-value test code for verifying the checkout. Anyone may use it.",
  },
};

/**
 * Legacy coupon codes mapped to the benefit type they really are. Codes keep
 * working exactly as today; this is how reporting and the migration path know
 * what each one means.
 */
export const LEGACY_CODE_BENEFIT_TYPE: Record<string, BenefitType> = {
  INDEPENDENCE: "PUBLIC_CAMPAIGN",
  IND79: "PUBLIC_CAMPAIGN",
  IND80: "PUBLIC_CAMPAIGN",
  FESTIVAL30: "PUBLIC_CAMPAIGN",
  FESTIVAL80: "PUBLIC_CAMPAIGN",
  VKITLOVE: "PUBLIC_CAMPAIGN",
  NRECT50LOVE: "SCHOLARSHIP",
  NRECT50LOVEGALS: "SCHOLARSHIP",
  VKITFNF: "PRIVATE_INVITATION",
  VKITPTNRKID: "PARTNER",
  VKITPTNRINTLKID: "PARTNER",
  VKITPTNRSCL: "PARTNER",
  "REF-WEKITEER": "REFERRAL",
  TEST99: "ADMIN",
};

/**
 * Codes that will retire once their account-based replacement is live. They
 * still price correctly today — the engine simply flags them so the admin hub
 * can show migration progress.
 */
export const MIGRATING_CODES: Array<{
  code: string;
  to: BenefitType;
  route: string;
}> = [
  {
    code: "NRECT50LOVE",
    to: "SCHOLARSHIP",
    route: "Scholarship application → admin approval → account entitlement",
  },
  {
    code: "NRECT50LOVEGALS",
    to: "SCHOLARSHIP",
    route: "Scholarship application → admin approval → account entitlement",
  },
  {
    code: "VKITPTNRKID",
    to: "PARTNER",
    route: "Partner ID → linked child → automatic partner pricing",
  },
  {
    code: "VKITPTNRINTLKID",
    to: "PARTNER",
    route: "Partner ID → linked child → automatic partner pricing (international)",
  },
  {
    code: "VKITPTNRSCL",
    to: "PARTNER",
    route: "Partner ID → linked school → automatic partner pricing",
  },
  {
    code: "VKITFNF",
    to: "PRIVATE_INVITATION",
    route: "Unique per-person invitation codes (FNF-XXXXXX)",
  },
];

/** Prefixes used when generating invitation codes. */
export const INVITATION_PREFIXES: Record<string, string> = {
  friends_family: "FNF",
  partner: "PTNR",
  institution: "INST",
  ambassador: "AMB",
  press: "PRESS",
};

/** Batch sizes the admin hub offers. */
export const INVITATION_BATCH_SIZES = [10, 100, 1000] as const;

/** Alphabet for generated codes — no vowels, no look-alike characters. */
export const INVITATION_ALPHABET = "23456789BCDFGHJKLMNPQRSTVWXZ";

/** How many distinct accounts from one IP before we flag it. */
export const FRAUD_IP_ACCOUNT_THRESHOLD = 3;

export function benefitRule(type: BenefitType): BenefitTypeRule {
  return BENEFIT_TYPE_RULES[type] ?? BENEFIT_TYPE_RULES.PUBLIC_CAMPAIGN;
}

/** The benefit type a legacy code represents. */
export function benefitTypeForCode(code: string): BenefitType {
  const c = String(code || "").trim().toUpperCase();
  if (LEGACY_CODE_BENEFIT_TYPE[c]) return LEGACY_CODE_BENEFIT_TYPE[c];
  if (c.startsWith("REF-")) return "REFERRAL";
  if (c.startsWith("FNF-")) return "PRIVATE_INVITATION";
  if (c.startsWith("PTNR-")) return "PARTNER";
  if (c.startsWith("INST-")) return "INSTITUTION";
  if (c.startsWith("AMB-")) return "AMBASSADOR";
  return "PUBLIC_CAMPAIGN";
}
